import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Menu, LogOut, Home, Users, ClipboardList, BarChart3, Newspaper, Search, Plus, X, 
  Phone, Mail, Calendar, Tag, MessageSquare, Trash2, GripVertical,
  ChevronDown, Check, MessageCircle, Download, UserPlus, TrendingUp, CheckCircle2, Bot
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const ETIQUETAS = [
  { id: "quente", nome: "Quente", cor: "#ef4444" },
  { id: "morno", nome: "Morno", cor: "#f59e0b" },
  { id: "frio", nome: "Frio", cor: "#3b82f6" },
  { id: "vip", nome: "VIP", cor: "#8b5cf6" },
  { id: "retornar", nome: "Retornar", cor: "#10b981" },
  { id: "urgente", nome: "Urgente", cor: "#dc2626" },
  { id: "quiz", nome: "Quiz", cor: "#ec4899" },
  { id: "agente", nome: "Agente Técnico", cor: "#22d3ee" },
];

const STATUS_COLUNAS = [
  { id: "novo", nome: "Novo Lead", cor: "#22c55e", headerBg: "bg-green-500/30" },
  { id: "contato_inicial", nome: "Contato Inicial", cor: "#3b82f6", headerBg: "" },
  { id: "em_negociacao", nome: "Em Negociação", cor: "#f59e0b", headerBg: "" },
  { id: "convertido", nome: "Convertido", cor: "#10b981", headerBg: "" },
  { id: "perdido", nome: "Perdido", cor: "#ef4444", headerBg: "" },
];

// Tipo de inscrito (origem do lead) para o filtro do Kanban
const getLeadTipo = (lead) => {
  const etq = lead.etiquetas || [];
  if (etq.includes('quiz') || lead.origem === 'quiz' || typeof lead.quiz_pontuacao === 'number') return 'quiz';
  if (etq.includes('agente') || lead.origem === 'agente_tecnico') return 'agente';
  return 'mentoria';
};

const TIPOS_INSCRITO = [
  { id: 'todos', nome: 'Todos', cor: '#eab308' },
  { id: 'mentoria', nome: 'Mentoria (vaga)', cor: '#3b82f6' },
  { id: 'quiz', nome: 'Quiz', cor: '#ec4899' },
  { id: 'agente', nome: 'Acesso IA', cor: '#22d3ee' },
];

// Templates de mensagem para WhatsApp (automação rápida)
const WA_TEMPLATES = [
  {
    id: 'boas_vindas',
    label: 'Boas-vindas',
    build: (nome) =>
      `Olá ${nome}! Aqui é da WING Academy Brasil 👋\nVi sua inscrição para a Mentoria em Grupo — bem-vindo(a)!\nPosso te passar os próximos passos?`
  },
  {
    id: 'link_pagamento',
    label: 'Link de pagamento',
    build: (nome) =>
      `Oi ${nome}! Segue o link oficial para garantir sua vaga na Mentoria WING Academy:\nhttps://mpago.la/1PRePGE\n\nQualquer dúvida é só me chamar. 🚀`
  },
  {
    id: 'follow_up',
    label: 'Follow-up (em negociação)',
    build: (nome) =>
      `Oi ${nome}, tudo bem?\nPassando aqui pra saber se ficou alguma dúvida sobre a mentoria. Posso te ajudar com qualquer informação antes de você confirmar sua vaga. 🙂`
  },
  {
    id: 'ultima_chance',
    label: 'Última chance',
    build: (nome) =>
      `${nome}, a turma está quase fechando! Restam poucas vagas com o desconto promocional.\nPosso te garantir uma? ⏳`
  }
];

// Helper: normaliza número para o formato internacional BR
const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // Se já vem com 55 ou país diferente, mantém
  if (digits.startsWith('55') && digits.length >= 12) return digits;
  return `55${digits}`;
};

const buildWaUrl = (phone, message) =>
  `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(message)}`;

// CSV export helpers
const csvEscape = (val) => {
  const s = val === null || val === undefined ? '' : String(val);
  return `"${s.replace(/"/g, '""')}"`;
};

const STATUS_LABELS = {
  novo: 'Novo Lead', contato_inicial: 'Contato Inicial',
  em_negociacao: 'Em Negociação', convertido: 'Convertido', perdido: 'Perdido'
};

const exportLeadsToCSV = (leads) => {
  const headers = ['Nome', 'Email', 'Telefone', 'Tipo', 'Status', 'Etiquetas', 'Experiência', 'Aluno WING', 'Quiz Pontuação', 'Quiz %', 'Mensagem', 'Anotações', 'Data de Criação'];
  const rows = leads.map((l) => [
    l.nome, l.email, l.telefone,
    l.tipo_mentoria === 'mentoria_grupo' ? 'Mentoria em Grupo' : 'Treinamento Individual',
    STATUS_LABELS[l.status || 'novo'] || l.status,
    (l.etiquetas || []).join(', '),
    l.experiencia || '',
    l.aluno_wing === 'sim' ? 'Sim' : (l.aluno_wing === 'nao' ? 'Não' : ''),
    typeof l.quiz_pontuacao === 'number' ? `${l.quiz_pontuacao}/${l.quiz_total}` : '',
    typeof l.quiz_percentual === 'number' ? `${l.quiz_percentual.toFixed(0)}%` : '',
    l.mensagem || '', l.anotacoes || '',
    l.data_criacao ? new Date(l.data_criacao).toLocaleString('pt-BR') : ''
  ]);
  const csv = '\uFEFF' + [headers, ...rows].map((r) => r.map(csvEscape).join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leads-wing-academy-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return Date.now() - d.getTime() <= 7 * 24 * 60 * 60 * 1000;
};

const getQuizBadgeClass = (pct) => {
  if (pct >= 70) return 'bg-green-500/20 text-green-400';
  if (pct >= 50) return 'bg-yellow-500/20 text-yellow-400';
  return 'bg-orange-500/20 text-orange-400';
};

const getQuizGradeLabel = (pct) => {
  if (pct >= 90) return '🏆 Excelente';
  if (pct >= 70) return '⭐ Muito Bom';
  if (pct >= 50) return '👍 Bom';
  return '📚 Em Evolução';
};

const AdminLeadsKanban = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        localStorage.removeItem('admin_token');
        fetchLeads();
      } catch (error) {
        logger.error('Falha na verificação de auth:', error);
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads?limit=500`, { withCredentials: true });
      // Garantir que todos os leads tenham status
      const leadsWithStatus = response.data.map(lead => ({
        ...lead,
        status: lead.status || 'novo',
        etiquetas: lead.etiquetas || [],
        anotacoes: lead.anotacoes || ''
      }));
      setLeads(leadsWithStatus);
    } catch (error) {
      logger.error('Erro ao buscar leads:', error);
      toast({ title: "Erro ao carregar leads", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const updateLead = async (leadId, updates) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/leads/${leadId}`, updates, { withCredentials: true });
      setLeads(leads.map(l => l.id === leadId ? { ...l, ...updates } : l));
      toast({ title: "Lead atualizado!" });
    } catch (error) {
      logger.error('Erro ao atualizar lead:', error);
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/leads/${leadId}`, { withCredentials: true });
      setLeads(leads.filter(l => l.id !== leadId));
      setSelectedLead(null);
      toast({ title: "Lead excluído!" });
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== newStatus) {
      await updateLead(draggedLead.id, { status: newStatus });
    }
    setDraggedLead(null);
  };

  const matchBusca = (lead) =>
    lead.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.telefone.includes(searchTerm);

  const searchedLeads = leads.filter(matchBusca);
  const tipoCounts = {
    todos: searchedLeads.length,
    mentoria: searchedLeads.filter(l => getLeadTipo(l) === 'mentoria').length,
    quiz: searchedLeads.filter(l => getLeadTipo(l) === 'quiz').length,
    agente: searchedLeads.filter(l => getLeadTipo(l) === 'agente').length,
  };

  const filteredLeads = searchedLeads.filter(lead =>
    tipoFiltro === 'todos' || getLeadTipo(lead) === tipoFiltro
  );

  const getLeadsByStatus = (status) => {
    return filteredLeads.filter(lead => (lead.status || 'novo') === status);
  };

  const weekLeads = leads.filter(l => isThisWeek(l.data_criacao));
  const weeklyStats = {
    novos: weekLeads.length,
    atendidos: weekLeads.filter(l => (l.status || 'novo') !== 'novo').length,
    taxa: weekLeads.length ? Math.round((weekLeads.filter(l => (l.status || 'novo') !== 'novo').length / weekLeads.length) * 100) : 0
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-16 md:w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300 flex-shrink-0`}>
        <div className="p-3 md:p-4">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            {sidebarOpen && <h1 className="text-lg md:text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2">
              <Menu className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
          <AdminNav active="leads" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-800 p-3 md:p-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">Gestão de Leads</h1>
              <p className="text-gray-400 text-xs md:text-sm">{leads.length} leads • Arraste para mudar status</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar lead..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                />
              </div>
              <button
                onClick={() => {
                  exportLeadsToCSV(filteredLeads);
                  toast({ title: `CSV exportado (${filteredLeads.length} leads)!` });
                }}
                data-testid="export-csv-btn"
                className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-lg transition-colors text-sm whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                Exportar CSV
              </button>
            </div>
          </div>

          {/* Métricas da semana */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3 mt-3">
            <div data-testid="metric-novos-semana" className="flex items-center gap-2.5 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <div className="text-lg font-bold text-white leading-none">{weeklyStats.novos}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">Novos na semana</div>
              </div>
            </div>
            <div data-testid="metric-atendidos-semana" className="flex items-center gap-2.5 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <div className="text-lg font-bold text-white leading-none">{weeklyStats.atendidos}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">Atendidos na semana</div>
              </div>
            </div>
            <div data-testid="metric-taxa-semana" className="hidden sm:flex items-center gap-2.5 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <div className="text-lg font-bold text-white leading-none">{weeklyStats.taxa}%</div>
                <div className="text-[11px] text-gray-400 mt-0.5">Taxa de atendimento</div>
              </div>
            </div>
          </div>

          {/* Filtro por tipo de inscrito */}
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="lead-tipo-filtro">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mr-1">Tipo:</span>
            {TIPOS_INSCRITO.map(t => {
              const ativo = tipoFiltro === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTipoFiltro(t.id)}
                  data-testid={`filtro-tipo-${t.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                    ativo ? 'text-black' : 'text-gray-300 border-gray-700 hover:border-gray-500'
                  }`}
                  style={ativo ? { backgroundColor: t.cor, borderColor: t.cor } : {}}
                >
                  {t.nome}
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${ativo ? 'bg-black/20' : 'bg-gray-700 text-gray-300'}`}>
                    {tipoCounts[t.id]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Kanban Board */}
        <div className="flex-1 overflow-x-auto p-3 md:p-4">
          <div className="flex gap-3 md:gap-4 min-w-max h-full">
            {STATUS_COLUNAS.map(coluna => (
              <div
                key={coluna.id}
                className="w-72 md:w-80 flex-shrink-0 flex flex-col bg-gray-900/50 rounded-xl border border-gray-800"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, coluna.id)}
              >
                {/* Column Header */}
                <div className={`p-3 md:p-4 border-b border-gray-800 rounded-t-xl ${coluna.headerBg || ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: coluna.cor }}></div>
                      <h3 className="font-semibold text-white text-sm md:text-base">{coluna.nome}</h3>
                    </div>
                    <span className="text-xs md:text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded-full">
                      {getLeadsByStatus(coluna.id).length}
                    </span>
                  </div>
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[calc(100vh-220px)]">
                  {getLeadsByStatus(coluna.id).map(lead => (
                    <div
                      key={lead.id}
                      data-testid={`lead-card-${lead.id}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      onClick={() => setSelectedLead(lead)}
                      className={`bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-750 transition-all border-2 ${
                        draggedLead?.id === lead.id ? 'border-yellow-500 opacity-50' : 'border-transparent hover:border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <GripVertical className="w-4 h-4 text-gray-600 cursor-grab flex-shrink-0" />
                        <div className="flex-1 ml-2">
                          <h4 className="font-medium text-white text-sm truncate">{lead.nome}</h4>
                          <p className="text-xs text-gray-400 truncate">{lead.email}</p>
                        </div>
                      </div>

                      {/* Etiquetas */}
                      {lead.etiquetas && lead.etiquetas.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {lead.etiquetas.map(etqId => {
                            const etq = ETIQUETAS.find(e => e.id === etqId);
                            return etq ? (
                              <span
                                key={etqId}
                                className="text-[10px] px-1.5 py-0.5 rounded text-white font-medium"
                                style={{ backgroundColor: etq.cor }}
                              >
                                {etq.nome}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}

                      {/* Quiz Score Badge */}
                      {typeof lead.quiz_pontuacao === 'number' && (
                        <div
                          data-testid={`lead-card-quiz-${lead.id}`}
                          className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 px-2 py-1 rounded"
                        >
                          🎯 Quiz: {lead.quiz_pontuacao}/{lead.quiz_total}
                          {typeof lead.quiz_percentual === 'number' && (
                            <span className="text-yellow-400/70">
                              ({lead.quiz_percentual.toFixed(0)}%)
                            </span>
                          )}
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className={`px-1.5 py-0.5 rounded ${
                          lead.tipo_mentoria === 'mentoria_grupo' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {lead.tipo_mentoria === 'mentoria_grupo' ? 'Grupo' : 'Individual'}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {lead.anotacoes && <MessageSquare className="w-3 h-3 text-yellow-400" />}
                          <a
                            href={buildWaUrl(lead.telefone, WA_TEMPLATES[0].build((lead.nome || '').split(' ')[0] || ''))}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Enviar WhatsApp (Boas-vindas)"
                            data-testid={`lead-card-wa-${lead.id}`}
                            className="w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors"
                          >
                            <MessageCircle className="w-3.5 h-3.5 text-white" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={(updates) => {
            updateLead(selectedLead.id, updates);
            setSelectedLead({ ...selectedLead, ...updates });
          }}
          onDelete={() => deleteLead(selectedLead.id)}
        />
      )}
    </div>
  );
};

// Modal de Detalhes do Lead
const LeadDetailModal = ({ lead, onClose, onUpdate, onDelete }) => {
  const [anotacoes, setAnotacoes] = useState(lead.anotacoes || '');
  const [showEtiquetas, setShowEtiquetas] = useState(false);

  const toggleEtiqueta = (etqId) => {
    const currentEtiquetas = lead.etiquetas || [];
    const newEtiquetas = currentEtiquetas.includes(etqId)
      ? currentEtiquetas.filter(id => id !== etqId)
      : [...currentEtiquetas, etqId];
    onUpdate({ etiquetas: newEtiquetas });
  };

  const handleSaveNotes = () => {
    onUpdate({ anotacoes });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Detalhes do Lead</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Info Principal */}
          <div>
            <h3 className="text-xl font-bold text-white mb-1">{lead.nome}</h3>
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2 text-gray-400">
                <Mail className="w-4 h-4" /> {lead.email}
              </p>
              <p className="flex items-center gap-2 text-gray-400">
                <Phone className="w-4 h-4" /> {lead.telefone}
              </p>
              <p className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-4 h-4" /> {new Date(lead.data_criacao).toLocaleDateString('pt-BR')}
              </p>
              {lead.experiencia && (
                <p className="text-gray-400">
                  <span className="text-gray-500">Nível:</span>{' '}
                  <span className="text-white capitalize">{lead.experiencia}</span>
                </p>
              )}
              {lead.aluno_wing && (
                <p className="text-gray-400" data-testid="lead-aluno-wing">
                  <span className="text-gray-500">Já é aluno WING?</span>{' '}
                  <span className={`font-semibold uppercase ${lead.aluno_wing === 'sim' ? 'text-green-400' : 'text-orange-400'}`}>
                    {lead.aluno_wing === 'sim' ? 'Sim' : 'Não'}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Mensagem do Lead */}
          {lead.mensagem && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Mensagem do Lead</label>
              <div
                data-testid="lead-message-display"
                className="bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-3 text-sm text-gray-200 whitespace-pre-wrap"
              >
                {lead.mensagem}
              </div>
            </div>
          )}

          {/* Consentimento LGPD */}
          {lead.consentimento?.aceito && (
            <div data-testid="lead-consent-display">
              <label className="block text-sm font-medium text-gray-400 mb-2">Consentimento LGPD</label>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 text-sm text-green-300">
                Aceito em {new Date(lead.consentimento.data_hora).toLocaleString('pt-BR')} · Política {lead.consentimento.versao_politica} · Origem: {lead.consentimento.origem}
              </div>
            </div>
          )}

          {/* Resultado do Quiz */}
          {typeof lead.quiz_pontuacao === 'number' && (
            <div data-testid="lead-quiz-result">
              <label className="block text-sm font-medium text-gray-400 mb-2">Resultado do Quiz</label>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {lead.quiz_pontuacao}/{lead.quiz_total}
                  </div>
                  {typeof lead.quiz_percentual === 'number' && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {lead.quiz_percentual.toFixed(0)}% de acertos
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getQuizBadgeClass(lead.quiz_percentual)}`}
                  >
                    {getQuizGradeLabel(lead.quiz_percentual)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STATUS_COLUNAS.map(status => (
                <button
                  key={status.id}
                  onClick={() => onUpdate({ status: status.id })}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    lead.status === status.id
                      ? 'ring-2 ring-offset-2 ring-offset-gray-900'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  style={{ 
                    backgroundColor: status.cor + '30', 
                    color: status.cor,
                    ringColor: lead.status === status.id ? status.cor : 'transparent'
                  }}
                >
                  {status.nome}
                </button>
              ))}
            </div>
          </div>

          {/* Etiquetas */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Etiquetas</label>
            <div className="flex flex-wrap gap-2">
              {ETIQUETAS.map(etq => {
                const isSelected = (lead.etiquetas || []).includes(etq.id);
                return (
                  <button
                    key={etq.id}
                    onClick={() => toggleEtiqueta(etq.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isSelected ? 'ring-2 ring-offset-1 ring-offset-gray-900' : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{ 
                      backgroundColor: etq.cor + '30', 
                      color: etq.cor,
                      ringColor: isSelected ? etq.cor : 'transparent'
                    }}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {etq.nome}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Tipo de Mentoria</label>
            <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium ${
              lead.tipo_mentoria === 'mentoria_grupo' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'bg-purple-500/20 text-purple-400'
            }`}>
              {lead.tipo_mentoria === 'mentoria_grupo' ? 'Mentoria em Grupo' : 'Treinamento Individual'}
            </span>
          </div>

          {/* Anotações */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Anotações</label>
            <textarea
              value={anotacoes}
              onChange={(e) => setAnotacoes(e.target.value)}
              placeholder="Adicione suas anotações sobre este lead..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none"
              rows={4}
            />
            <button
              onClick={handleSaveNotes}
              className="mt-2 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-medium py-2 rounded-lg transition-colors text-sm"
            >
              Salvar Anotações
            </button>
          </div>

          {/* WhatsApp Templates (Automação) */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-400" />
              Enviar mensagem WhatsApp
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {WA_TEMPLATES.map(tpl => {
                const firstName = (lead.nome || '').trim().split(' ')[0] || '';
                const url = buildWaUrl(lead.telefone, tpl.build(firstName));
                return (
                  <a
                    key={tpl.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wa-template-${tpl.id}`}
                    className="flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{tpl.label}</span>
                  </a>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Clique para abrir o WhatsApp com a mensagem já pronta para este lead.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 flex gap-3">
          <a
            href={buildWaUrl(lead.telefone, `Olá ${(lead.nome || '').split(' ')[0] || ''}! Aqui é da WING Academy Brasil.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lead-modal-wa-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2.5 rounded-lg transition-colors text-center text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Abrir WhatsApp
          </a>
          <button
            onClick={onDelete}
            data-testid="lead-modal-delete-btn"
            className="px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLeadsKanban;
